import { useState, useEffect } from 'react';
import { X, Upload, Trash2, Star, Plus } from 'lucide-react';
import { Button } from '@/app/components/ui/button';
import type { Project } from '@/data/projects';
import type { Category } from '@/services/api';
import { uploadImage } from '@/services/api';

interface ProjectEditModalProps {
  project: Project | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (project: Partial<Project>) => void;
  onCreate?: (project: Omit<Project, 'id'>) => void;
  categories?: Category[];
}

export function ProjectEditModal({ project, isOpen, onClose, onUpdate, onCreate, categories = [] }: ProjectEditModalProps) {
  const [formData, setFormData] = useState({
    titleHu: '',
    titleEn: '',
    category: 'residential' as string,
    clientHu: '',
    clientEn: '',
    location: '',
    designYear: '',
    completionYear: '',
    area: '',
    descriptionHu: '',
    descriptionEn: '',
    customFields: [] as Array<{
      title: { hu: string; en: string };
      content: { hu: string; en: string };
    }>,
    images: [] as string[]
  });
  
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [imageDimensions, setImageDimensions] = useState<Record<string, { width: number; height: number }>>({});
  
  const isCreateMode = !project;

  useEffect(() => {
    if (project) {
      setFormData({
        titleHu: project.title.hu,
        titleEn: project.title.en,
        category: project.category,
        clientHu: project.client?.hu || '',
        clientEn: project.client?.en || '',
        location: project.location,
        designYear: project.designYear || '',
        completionYear: project.completionYear || '',
        area: project.area,
        descriptionHu: project.description.hu,
        descriptionEn: project.description.en,
        customFields: project.customFields || [],
        images: project.images
      });
    } else {
      // Reset form for create mode
      setFormData({
        titleHu: '',
        titleEn: '',
        category: categories.length > 0 ? categories[0].id : 'residential',
        clientHu: '',
        clientEn: '',
        location: '',
        designYear: new Date().getFullYear().toString(),
        completionYear: '',
        area: '',
        descriptionHu: '',
        descriptionEn: '',
        customFields: [],
        images: []
      });
    }
  }, [project, categories]);

  // Load image dimensions when images change
  useEffect(() => {
    const loadImageDimensions = async () => {
      const dimensions: Record<string, { width: number; height: number }> = {};
      
      for (const imageUrl of formData.images) {
        // Skip if already loaded
        if (imageDimensions[imageUrl]) {
          dimensions[imageUrl] = imageDimensions[imageUrl];
          continue;
        }
        
        try {
          const img = new Image();
          await new Promise<void>((resolve, reject) => {
            img.onload = () => {
              dimensions[imageUrl] = { width: img.naturalWidth, height: img.naturalHeight };
              resolve();
            };
            img.onerror = reject;
            img.src = imageUrl;
          });
        } catch (error) {
          console.error('Failed to load image dimensions:', error);
        }
      }
      
      setImageDimensions(prev => ({ ...prev, ...dimensions }));
    };
    
    if (formData.images.length > 0) {
      loadImageDimensions();
    }
  }, [formData.images]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.images.length === 0) {
      alert('Legalább egy képet fel kell töltened!');
      return;
    }
    
    const projectData: any = {
      title: {
        hu: formData.titleHu,
        en: formData.titleEn
      },
      category: formData.category,
      location: formData.location,
      area: formData.area,
      description: {
        hu: formData.descriptionHu,
        en: formData.descriptionEn
      },
      images: formData.images
    };

    // Only include optional fields if they have values
    if (formData.clientHu || formData.clientEn) {
      projectData.client = {
        hu: formData.clientHu,
        en: formData.clientEn
      };
    }

    // Always include year fields - use null to explicitly clear them (undefined gets removed by JSON.stringify)
    projectData.designYear = formData.designYear || null;
    projectData.completionYear = formData.completionYear || null;

    if (formData.customFields.length > 0) {
      projectData.customFields = formData.customFields;
    }

    if (isCreateMode && onCreate) {
      onCreate(projectData);
    } else {
      onUpdate(projectData);
    }
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploading(true);
    setUploadProgress('Képek feltöltése...');
    
    const uploadedUrls: string[] = [];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      setUploadProgress(`Kép feltöltése ${i + 1}/${files.length}...`);
      
      try {
        const url = await uploadImage(file);
        if (url) {
          uploadedUrls.push(url);
        }
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Ismeretlen hiba';
        alert(`Hiba történt a(z) "${file.name}" fájl feltöltésekor:\n\n${errorMessage}`);
        console.error('Upload error:', error);
      }
    }
    
    if (uploadedUrls.length > 0) {
      setFormData({ ...formData, images: [...formData.images, ...uploadedUrls] });
    }
    
    setUploading(false);
    setUploadProgress('');
    
    // Reset file input
    e.target.value = '';
  };

  const removeImage = (index: number) => {
    const newImages = formData.images.filter((_, i) => i !== index);
    setFormData({ ...formData, images: newImages });
  };

  const moveImageToFirst = (index: number) => {
    const newImages = [...formData.images];
    const [movedImage] = newImages.splice(index, 1);
    newImages.unshift(movedImage);
    setFormData({ ...formData, images: newImages });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold">
            {isCreateMode ? 'Új Projekt Hozzáadása' : 'Projekt Szerkesztése'}
          </h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Title */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cím (Magyar)
              </label>
              <input
                type="text"
                value={formData.titleHu}
                onChange={(e) => setFormData({ ...formData, titleHu: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Title (English)
              </label>
              <input
                type="text"
                value={formData.titleEn}
                onChange={(e) => setFormData({ ...formData, titleEn: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
          </div>

          {/* Client Name */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ügyfél neve (Magyar) <span className="text-gray-400 text-xs">(opcionális)</span>
              </label>
              <input
                type="text"
                value={formData.clientHu}
                onChange={(e) => setFormData({ ...formData, clientHu: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Client Name (English) <span className="text-gray-400 text-xs">(optional)</span>
              </label>
              <input
                type="text"
                value={formData.clientEn}
                onChange={(e) => setFormData({ ...formData, clientEn: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
              />
            </div>
          </div>

          {/* Category, Location */}
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Kategória
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                required
              >
                {categories.length > 0 ? (
                  categories.map(category => (
                    <option key={category.id} value={category.id}>{category.name.hu}</option>
                  ))
                ) : (
                  <>
                    <option value="residential">Lakóépület</option>
                    <option value="commercial">Kereskedelmi</option>
                    <option value="interior">Belső tér</option>
                  </>
                )}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Helyszín
              </label>
              <input
                type="text"
                value={formData.location}
                onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Terület
              </label>
              <input
                type="text"
                value={formData.area}
                onChange={(e) => setFormData({ ...formData, area: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="pl. 450 m²"
                required
              />
            </div>
          </div>

          {/* Design Year & Completion Year */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tervezés éve <span className="text-gray-400 text-xs">(opcionális)</span>
              </label>
              <input
                type="text"
                value={formData.designYear}
                onChange={(e) => setFormData({ ...formData, designYear: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="pl. 2023"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Megvalósulás éve <span className="text-gray-400 text-xs">(opcionális)</span>
              </label>
              <input
                type="text"
                value={formData.completionYear}
                onChange={(e) => setFormData({ ...formData, completionYear: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                placeholder="pl. 2024"
              />
            </div>
          </div>

          {/* Description */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Leírás (Magyar)
              </label>
              <textarea
                value={formData.descriptionHu}
                onChange={(e) => setFormData({ ...formData, descriptionHu: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 h-32"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description (English)
              </label>
              <textarea
                value={formData.descriptionEn}
                onChange={(e) => setFormData({ ...formData, descriptionEn: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-2 h-32"
                required
              />
            </div>
          </div>

          {/* Custom Fields */}
          <div className="border-t border-gray-200 pt-6">
            <div className="flex items-center justify-between mb-4">
              <label className="block text-sm font-medium text-gray-700">
                Egyedi mezők <span className="text-gray-400 text-xs">(opcionális)</span>
              </label>
              <button
                type="button"
                onClick={() => {
                  setFormData({
                    ...formData,
                    customFields: [
                      ...formData.customFields,
                      { title: { hu: '', en: '' }, content: { hu: '', en: '' } }
                    ]
                  });
                }}
                className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 transition-colors flex items-center gap-1"
              >
                <Plus size={16} />
                <span>Új mező</span>
              </button>
            </div>

            {formData.customFields.length === 0 && (
              <p className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-md">
                Még nincs egyedi mező. Kattints az "Új mező" gombra egyedi mezők hozzáadásához.
              </p>
            )}

            <div className="space-y-4">
              {formData.customFields.map((field, index) => (
                <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-700">Mező #{index + 1}</h4>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData({
                          ...formData,
                          customFields: formData.customFields.filter((_, i) => i !== index)
                        });
                      }}
                      className="text-red-600 hover:text-red-800 p-1"
                      title="Mező törlése"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>

                  {/* Field Title */}
                  <div className="grid md:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Mező címe (Magyar)
                      </label>
                      <input
                        type="text"
                        value={field.title.hu}
                        onChange={(e) => {
                          const newFields = [...formData.customFields];
                          newFields[index].title.hu = e.target.value;
                          setFormData({ ...formData, customFields: newFields });
                        }}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        placeholder="pl. Építész"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Field Title (English)
                      </label>
                      <input
                        type="text"
                        value={field.title.en}
                        onChange={(e) => {
                          const newFields = [...formData.customFields];
                          newFields[index].title.en = e.target.value;
                          setFormData({ ...formData, customFields: newFields });
                        }}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        placeholder="e.g. Architect"
                      />
                    </div>
                  </div>

                  {/* Field Content */}
                  <div className="grid md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Tartalom (Magyar)
                      </label>
                      <input
                        type="text"
                        value={field.content.hu}
                        onChange={(e) => {
                          const newFields = [...formData.customFields];
                          newFields[index].content.hu = e.target.value;
                          setFormData({ ...formData, customFields: newFields });
                        }}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        placeholder="pl. Kiss János"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">
                        Content (English)
                      </label>
                      <input
                        type="text"
                        value={field.content.en}
                        onChange={(e) => {
                          const newFields = [...formData.customFields];
                          newFields[index].content.en = e.target.value;
                          setFormData({ ...formData, customFields: newFields });
                        }}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        placeholder="e.g. John Smith"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Images Upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Projekt képek {formData.images.length > 0 && `(${formData.images.length})`}
            </label>
            
            <div className="space-y-4">
              {/* Upload button */}
              <div className="flex items-center gap-3">
                <label className="cursor-pointer">
                  <div className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                    <Upload size={18} />
                    <span>Képek feltöltése</span>
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleFileSelect}
                    className="hidden"
                    disabled={uploading}
                  />
                </label>
                
                {uploading && (
                  <span className="text-sm text-gray-600">{uploadProgress}</span>
                )}
              </div>
              
              {formData.images.length === 0 && (
                <p className="text-sm text-gray-500">
                  Még nincs feltöltött kép. Legalább egy kép szükséges.
                </p>
              )}
              
              {/* Image thumbnails */}
              {formData.images.length > 0 && (
                <div>
                  <p className="text-sm text-gray-600 mb-3">
                    Az első kép jelenik meg elsődleges képként a projekt oldalon. Húzd az képeket az elsődleges pozícióba a csillag ikonra kattintva.
                  </p>
                  <div className="grid grid-cols-3 gap-4">
                    {formData.images.map((image, index) => (
                      <div key={index} className="relative group">
                        <div className="relative bg-gray-100 rounded-lg overflow-hidden">
                          <img
                            src={image}
                            alt={`Kép ${index + 1}`}
                            className="w-full h-auto object-contain"
                          />
                          
                          {/* Red crop indicators - show if image will be cropped on project detail page */}
                          {imageDimensions[image] && (() => {
                            const { width, height } = imageDimensions[image];
                            
                            // All images use md:h-[550px] object-cover object-[center_bottom]
                            // The container has max-w-7xl (1280px) with lg:px-8 (32px each side) and md:pl-[225px]
                            // Available width = 1280 - 32 - 32 - 225 = ~991px
                            // But the actual visible area might be slightly less
                            
                            const TARGET_HEIGHT = 550;
                            const CONTAINER_MAX_WIDTH = 1280;
                            const HORIZONTAL_PADDING = 64; // 32px left + 32px right (lg:px-8)
                            const IMAGE_LEFT_PADDING = 225;
                            const AVAILABLE_WIDTH = CONTAINER_MAX_WIDTH - HORIZONTAL_PADDING - IMAGE_LEFT_PADDING; // ~991px
                            
                            // Calculate the width when height is scaled to 550px
                            const scaledWidth = (width / height) * TARGET_HEIGHT;
                            
                            // If scaled width is wider than available container width, it will be cropped
                            const willBeCropped = scaledWidth > AVAILABLE_WIDTH;
                            
                            if (!willBeCropped) return null;
                            
                            // Calculate the percentage of width that will be visible
                            const visibleWidthRatio = AVAILABLE_WIDTH / scaledWidth;
                            const cropFromEachSide = (1 - visibleWidthRatio) / 2;
                            const leftCropPercentage = cropFromEachSide * 100;
                            const rightCropPercentage = cropFromEachSide * 100;
                            
                            return (
                              <>
                                {/* Left crop line */}
                                <div 
                                  className="absolute top-0 bottom-0 w-[1px] bg-red-500 z-10"
                                  style={{ left: `${leftCropPercentage}%` }}
                                />
                                {/* Right crop line */}
                                <div 
                                  className="absolute top-0 bottom-0 w-[1px] bg-red-500 z-10"
                                  style={{ right: `${rightCropPercentage}%` }}
                                />
                              </>
                            );
                          })()}
                          
                          {/* Image dimensions - bottom right of image */}
                          {imageDimensions[image] && (
                            <div className="absolute bottom-2 right-2 bg-black bg-opacity-70 text-white text-xs px-2 py-1 rounded">
                              {imageDimensions[image].width} × {imageDimensions[image].height}
                            </div>
                          )}
                        </div>
                        
                        {/* Primary badge */}
                        {index === 0 && (
                          <div className="absolute top-2 left-2 bg-yellow-500 text-white text-xs px-2 py-1 rounded-md flex items-center gap-1 z-10">
                            <Star size={12} fill="white" />
                            <span>Elsődleges</span>
                          </div>
                        )}
                        
                        {/* Action buttons */}
                        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                          {index !== 0 && (
                            <button
                              type="button"
                              onClick={() => moveImageToFirst(index)}
                              className="p-1.5 bg-yellow-500 text-white rounded hover:bg-yellow-600"
                              title="Beállítás elsődlegesként"
                            >
                              <Star size={16} />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="p-1.5 bg-red-600 text-white rounded hover:bg-red-700"
                            title="Törlés"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                        
                        <p className="text-xs text-gray-500 mt-1 text-center">Kép {index + 1}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={onClose}>
              Mégse
            </Button>
            <Button type="submit" disabled={uploading || formData.images.length === 0}>
              {isCreateMode ? 'Létrehozás' : 'Mentés'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}